import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { createNativeSupabaseClient } from "@/lib/supabase/native-client";
import { sanitizeReturnUrl } from "@/lib/oauth/return-url";
import { WebAuthSession } from "@/lib/auth/web-auth-session";
import { processAuthDeepLink } from "@/lib/auth/oauth-deep-link";
import { isLocale, locales } from "@/i18n/routing";
import { wrapWithCrewHandoff } from "@/lib/crew/auth-handoff-client";

/**
 * Capacitor 네이티브 앱에서 OAuth 로그인을 시작하는 헬퍼 모음.
 *
 * iOS 흐름 (ASWebAuthenticationSession):
 * 1. OAuth 시작 URL 을 얻음 (Supabase 는 SDK 로, Naver/LINE 은 서버 라우트로)
 * 2. `WebAuthSession.start({ url, callbackScheme: "locarora" })` 로 시트 표시
 * 3. provider 인증 후 `locarora://auth/callback#access_token=...` 으로 리다이렉트
 * 4. iOS 가 callbackScheme 매치되는 순간 시트 자동 dismiss 하고 callback URL 반환
 * 5. `processAuthDeepLink(callbackURL)` 로 토큰 추출 + setSession + 리다이렉트
 *
 * 왜 ASWebAuthenticationSession 인가:
 * - `@capacitor/browser` (= SFSafariViewController) 는 OAuth 후 custom scheme
 *   redirect 를 OS 핸들러에 안 넘기고 빈 페이지에서 멈춘다 (iOS 13+ 일부 케이스).
 *   ASWebAuthenticationSession 은 그 시나리오에 맞춰 만든 공식 API 라 callback
 *   매치 + 자동 dismiss 가 보장된다.
 *
 * Android: Browser.open (Chrome Custom Tabs) 그대로. 별 문제 없이 동작.
 *
 * 크루 클릭 핸드오프: 로그인 창의 첫 URL 은 언제나 `/api/crew/auth-handoff` 로 감싼다.
 * 그 창은 시스템 브라우저 쿠키 저장소(iOS=Safari, Android=Chrome)를 공유하므로, 방문자가
 * 브라우저에서 누른 크루 링크의 la_ref 가 거기 보인다. 라우트가 그것을 nonce 아래 맡기고
 * 곧장 원래 인증 URL 로 302 하며, setSession 직후 oauth-deep-link 가 같은 nonce 로 회수한다
 * (claimCrewHandoff). 크루 클릭이 없으면 라우트는 302 한 번뿐이다.
 */

const CALLBACK_SCHEME = "locarora";
const SUPABASE_NATIVE_CALLBACK = `${CALLBACK_SCHEME}://auth/callback`;

async function startOAuthSession(rawAuthUrl: string): Promise<void> {
  const authUrl = wrapWithCrewHandoff(rawAuthUrl);
  if (Capacitor.getPlatform() === "ios") {
    const result = await WebAuthSession.start({
      url: authUrl,
      callbackScheme: CALLBACK_SCHEME,
    });
    // Supabase Redirect URL 매치 실패 시 https 로 fallback 되면 ASWebAuthenticationSession
    // 이 dismiss 안 되거나 web URL 을 돌려주는데, 그러면 processAuthDeepLink 가 silent
    // false 반환. 미리 스킴을 검증해서 AuthForm catch 가 toast 를 띄울 수 있게 한다.
    if (!result.url?.startsWith(`${CALLBACK_SCHEME}://`)) {
      throw new Error(
        `OAuth callback returned unexpected scheme: ${result.url ?? "(empty)"}`
      );
    }
    await processAuthDeepLink(result.url);
    return;
  }
  // Android & 그 외 native runtime: 기존 Browser.open 흐름 유지
  await Browser.open({ url: authUrl });
}

export async function startNativeSupabaseOAuth(
  provider: "google" | "apple" | "kakao",
  returnUrl?: string,
  locale?: string,
): Promise<void> {
  // The callback path is what nativeOAuthPostSignup uses to infer the
  // user's preferred_language. If the caller passed a locale (i.e. the app
  // is currently set to e.g. "en"), make sure the next URL carries that
  // prefix so inferLocale() in processAuthDeepLink resolves correctly —
  // otherwise it falls back to "ko" and the user's language silently
  // reverts on first login.
  let safeNext = sanitizeReturnUrl(returnUrl);
  if (locale && isLocale(locale)) {
    const hasLocalePrefix = locales.some(
      (l) => safeNext === `/${l}` || safeNext.startsWith(`/${l}/`),
    );
    if (!hasLocalePrefix) {
      safeNext = safeNext === "/" ? `/${locale}` : `/${locale}${safeNext}`;
    }
  }
  const nativeClient = createNativeSupabaseClient();

  const { data, error } = await nativeClient.auth.signInWithOAuth({
    provider,
    options: {
      // Supabase Dashboard Redirect URLs 에 반드시 "locarora://auth/callback" + wildcard 등록
      redirectTo: `${SUPABASE_NATIVE_CALLBACK}?next=${encodeURIComponent(safeNext)}`,
      skipBrowserRedirect: true,
    },
  });

  if (error || !data?.url) {
    throw error ?? new Error("Supabase did not return an OAuth URL");
  }

  await startOAuthSession(data.url);
}

/**
 * Naver/LINE 은 우리 시작 라우트를 **로그인 창 안에서** 연다(redirect=1 → 공급자 URL 로 302).
 * 예전처럼 WebView 에서 JSON 을 받아 공급자 URL 을 직접 열면 창의 첫 URL 이 공급자라
 * 크루 핸드오프 라우트를 거칠 수 없다. state 는 HMAC 서명이라 어느 쿠키 저장소에서
 * 만들어도 같다. 시작 실패는 라우트가 locarora://auth/callback?error= 로 돌려 창을 닫는다.
 */
function buildNativeStartUrl(
  provider: "naver" | "line",
  returnUrl: string | undefined,
  locale: string,
): string {
  const params = new URLSearchParams({
    native: "1",
    redirect: "1",
    returnUrl: sanitizeReturnUrl(returnUrl),
    locale,
  });
  return `${window.location.origin}/api/auth/${provider}/start?${params.toString()}`;
}

export async function startNativeNaverOAuth(
  returnUrl: string | undefined,
  locale: string
): Promise<void> {
  await startOAuthSession(buildNativeStartUrl("naver", returnUrl, locale));
}

export async function startNativeLineOAuth(
  returnUrl: string | undefined,
  locale: string
): Promise<void> {
  await startOAuthSession(buildNativeStartUrl("line", returnUrl, locale));
}
